import { useEffect, useState } from 'react';
import { Button, FormGroup, Input, Label, Modal, ModalBody, ModalFooter } from 'reactstrap';
import WarningIcon from '@mui/icons-material/Warning';
import { enqueueSnackbar, closeSnackbar } from 'notistack';
import { useTranslation } from 'react-i18next';
import { IconButton } from '@mui/material';
import { Close as CloseIcon } from '@mui/icons-material';

import { useHubDeleteModalStore } from './HubsDeleteStore';
import { DeleteType } from './types';

import useDeleteHub from 'hooks/Traps/Hubs/useDeleteHub';
import { HubData } from 'api/traps/Hubs/types';

interface HubDeleteModalRefetchProps {
  refetchHubs: () => void;
  hubs?: HubData[];
}

const HubDeleteModal: React.FC<HubDeleteModalRefetchProps> = ({ hubs, refetchHubs }) => {
  const [level, setLevel] = useState(-1);
  const [step, setStep] = useState<number>(1);
  const [deleteType, setDeleteType] = useState<DeleteType | null>(null);
  const [targetHubId, setTargetHubId] = useState<number | null>(null);
  const { isOpen, hubName, hubPk, trapsConnected, closeDeleteHubModal: closeModal } = useHubDeleteModalStore();
  const { getDeleteById } = useDeleteHub();

  const { t } = useTranslation();

  const onReset = () => {
    setLevel(-1);
    setDeleteType(null);
    setTargetHubId(null);
    closeModal();
  };

  useEffect(() => {
    if (trapsConnected > 0) {
      setStep(1);
    } else {
      setStep(3);
    }
  }, [trapsConnected, closeModal, isOpen]);

  const onDelete = () => {
    getDeleteById(
      { hubId: hubPk, level, deleteType, targetHubId },
      {
        onSuccess: () => {
          enqueueSnackbar('Hub deleted', {
            variant: 'success',
            action: key => (
              <IconButton onClick={() => closeSnackbar(key)} color="inherit">
                <CloseIcon />
              </IconButton>
            ),
          });
          onReset();
          refetchHubs();
        },
        onError: (error: any) => {
          const errorMessage = error?.response?.data?.error || 'Error deleting hub';
          enqueueSnackbar(errorMessage, {
            variant: 'error',
            action: key => (
              <IconButton onClick={() => closeSnackbar(key)} color="inherit">
                <CloseIcon />
              </IconButton>
            ),
          });
          onReset();
        },
      },
    );
  };

  return (
    <div>
      <Modal isOpen={isOpen} toggle={onReset} size="md">
        <ModalBody>
          <div className="mt-4 pt-4 d-flex flex-column  justify-content-center">
            <div className="d-flex align-items-center justify-content-center">
              <WarningIcon color="warning" fontSize="large" style={{ width: '100px', height: '100px' }} />
            </div>

            {step === 1 ? (
              <>
                <h4 className="text-center">{`${t('TrapModals.deleteHubModal.hubDelete')} ${hubName}`}</h4>
                <h5 className="text-center">{t('TrapModals.deleteHubModal.deleteError')}</h5>
                <p className="text-center my-2 text-muted">{t('TrapModals.deleteHubModal.selectDeleteType')}</p>
                <div className="p-4">
                  <FormGroup className="mb-3" check inline>
                    <Input
                      type="radio"
                      radioGroup="deleteType"
                      checked={deleteType === DeleteType.DELETE}
                      onChange={() => setDeleteType(DeleteType.DELETE)}
                    />
                    <Label check>
                      <p className="mb-0 pb-0">{t('TrapModals.deleteHubModal.deleteHubTraps')}</p>
                    </Label>
                  </FormGroup>
                  <FormGroup className="mb-3" check inline>
                    <Input
                      type="radio"
                      radioGroup="deleteType"
                      checked={deleteType === DeleteType.MOVE}
                      disabled={hubs ? hubs.length === 1 : true}
                      onChange={() => setDeleteType(DeleteType.MOVE)}
                    />
                    <Label check>
                      <p className="mb-0 pb-0">{t('TrapModals.deleteHubModal.MoveTrapsToAnotherHub')}</p>
                    </Label>
                  </FormGroup>
                </div>
              </>
            ) : step === 2 ? (
              <>
                <h4 className="text-center">{`${t('TrapModals.deleteHubModal.hubDelete')} ${hubName}`}</h4>
                <p className="text-center my-2 text-muted">{t('TrapModals.deleteHubModal.selectHubToMove')}</p>
                <div className="p-4">
                  {hubs
                    ?.filter(hub => hub.id !== hubPk)
                    .map(hub => {
                      return (
                        <FormGroup className="mb-3" check inline key={hub.id}>
                          <Input
                            type="radio"
                            radioGroup="targetHub"
                            checked={targetHubId === hub.id}
                            onChange={() => setTargetHubId(hub.id)}
                          />
                          <Label>
                            <p className="mb-0 pb-0">
                              {t('TrapModals.deleteHubModal.hubSerialNumber', { hubSerialNumber: hub.serial_number })}
                            </p>
                          </Label>
                        </FormGroup>
                      );
                    })}
                </div>
              </>
            ) : (
              <>
                <h4 className="text-center">{`${t('TrapModals.deleteHubModal.hubDelete')} ${hubName}`}</h4>
                <p className="text-center my-2 text-muted">{t('TrapModals.deleteHubModal.choseDeleteOption')}</p>
                <div className="p-4">
                  <FormGroup className="mb-3" check inline>
                    <Input
                      type="checkbox"
                      {...(level > 1 ? { checked: true, disabled: true } : { checked: level === 1 })}
                      onChange={() => (level !== 1 ? setLevel(1) : setLevel(0))}
                    />
                    <Label check>
                      <p className="mb-0 pb-0">{t('TrapModals.deleteHubModal.firstOption')}</p>
                      <p className="text-muted">{t('TrapModals.deleteHubModal.firstOption__comment')}</p>
                    </Label>
                  </FormGroup>
                  <FormGroup className="mb-3" check inline>
                    <Input
                      type="checkbox"
                      {...(level > 2 ? { checked: true, disabled: true } : { checked: level === 2 })}
                      onChange={() => (level !== 2 ? setLevel(2) : setLevel(0))}
                    />
                    <Label check>
                      <p className="mb-0 pb-0">{t('TrapModals.deleteHubModal.secondOption')}</p>
                      <p className="text-muted">{t('TrapModals.deleteHubModal.secondOption__comment')}</p>
                    </Label>
                  </FormGroup>
                  <FormGroup className="mb-3" check inline>
                    <Input type="checkbox" onChange={() => (level !== 3 ? setLevel(3) : setLevel(0))} />
                    <Label check>
                      <p className="mb-0 pb-0">{t('TrapModals.deleteHubModal.thirdOption')}</p>
                      <p className="text-muted">{t('TrapModals.deleteHubModal.thirdOption__comment')}</p>
                    </Label>
                  </FormGroup>
                </div>
              </>
            )}
          </div>
        </ModalBody>
        <ModalFooter>
          {step !== 3 && (
            <div className="d-flex justify-end align-center gap-2">
              <Button
                className="btn btn-ghost-danger"
                onClick={() => {
                  setStep(1);
                  setDeleteType(null);
                  setTargetHubId(null);
                  closeModal();
                }}
              >
                {t('TrapModals.deleteHubModal.cancel')}
              </Button>
              <Button
                className="btn btn-ghost-success"
                disabled={deleteType === null}
                onClick={() => {
                  if (step === 1 && deleteType === DeleteType.MOVE) {
                    setStep(2);
                  } else {
                    setStep(3);
                  }
                }}
              >
                {t('TrapModals.deleteHubModal.next')}
              </Button>
            </div>
          )}
          {step === 3 && (
            <Button
              className="btn btn-ghost-danger"
              onClick={() => {
                onDelete();
              }}
              {...(level < 1 && { disabled: true })}
            >
              {t('TrapModals.deleteModal.remove')}
            </Button>
          )}
        </ModalFooter>
      </Modal>
    </div>
  );
};

export default HubDeleteModal;
